import { useMemo, useState } from 'react';
import PT from 'prop-types';
import IPT from 'react-immutable-proptypes';
import { Map } from 'immutable';

import { QUESTION_TYPES } from 'edrolo/constants/QuestionTypes';
import { useQuizStats } from 'edrolo/domains/lessons/hooks';
import { useInjectable } from 'core/hooks';
import { useAnalytics } from 'edrolo/analytics';

import JeddleSingleAnswerMultiChoiceQuestion from '../questionTypes/JeddleSingleAnswerMultiChoiceQuestion';
import JeddleFreeTextQuestion from '../questionTypes/JeddleFreeTextQuestion';
import {
  lessonPropType,
  quizAttemptPropType,
  quizQuestionPropType,
} from '../../../lessons/propTypes';

/**
 * UX modes for a Jeddle question.
 *  - unanswered: user is selecting an answer (no result revealed)
 *  - saving: user clicked Save; correctness is revealed but Continue is not yet shown
 *  - answered: question has a saved answer (either previously saved or just saved);
 *    show feedback + Continue
 */
const MODE = {
  unanswered: 'unanswered',
  saving: 'saving',
  answered: 'answered',
};

const getIsCorrectForAnswerId = (question, answerId) => {
  if (!answerId) {
    return null;
  }
  const answers = question.get('answers');
  const selected = answers?.find?.((a) => a.get('id') === answerId);
  return selected?.get('is_correct') ?? null;
};

const JeddleQuestion = ({
  question,
  lesson,
  onResponseSave,
  style,
  lessonQuizAttempts,
  saveQuizAttempt,
  nextChunkUrl,
  isLastChunk,
}) => {
  const { track } = useAnalytics();

  const questionId = question.get('id');
  const lessonId = lesson.get('id');
  const answerType = question.get('answer_type');

  const useQuizStatsInjectable = useInjectable(
    'useQuizStatsInjectable',
    useQuizStats
  );

  const {
    data: quizStats,
    isLoading,
    error,
  } = useQuizStatsInjectable(lessonId, questionId);

  // Pull the most recent saved answer id for this question (if any).
  const existingAnswerId = useMemo(() => {
    if (!lessonQuizAttempts) {
      return null;
    }
    const attemptsForQuestion = lessonQuizAttempts.filter(
      (qa) => qa.get('question') === questionId
    );
    return attemptsForQuestion.last()?.get('answer') ?? null;
  }, [lessonQuizAttempts, questionId]);

  const [isSaving, setIsSaving] = useState(false);
  const [mode, setMode] = useState(
    existingAnswerId ? MODE.answered : MODE.unanswered
  );
  const [response, setResponse] = useState(() =>
    existingAnswerId
      ? Map({ question: questionId, answer: existingAnswerId })
      : Map({ question: questionId })
  );

  // Correctness only applies to multi-choice; free-text is self-marked.
  const showResult = mode !== MODE.unanswered;
  const correctness =
    showResult && answerType === QUESTION_TYPES.SINGLE_ANSWER_MULTI_CHOICE
      ? getIsCorrectForAnswerId(question, response.get('answer'))
      : null;

  const handleResponseChange = (res) => {
    setResponse(res);
    setMode(MODE.unanswered);
  };

  const handleSaveComplete = () => {
    track('completed quiz', { quiz_question_id: questionId });
    setIsSaving(false);
    setMode(MODE.answered);
  };

  const handleSaveError = () => {
    // Reset UI so the user can retry the save. We deliberately revert to
    // `unanswered` (rather than leaving `saving` set) so the disabled state
    // on inputs/buttons is cleared.
    setIsSaving(false);
    setMode(MODE.unanswered);
  };

  const handleSaveClick = () => {
    setIsSaving(true);
    setMode(MODE.saving);

    saveQuizAttempt(
      lessonId,
      questionId,
      response,
      handleSaveComplete,
      handleSaveError
    );
  };

  const handleContinue = () => {
    if (isLastChunk && nextChunkUrl) {
      window.location.href = nextChunkUrl;
      return;
    }
    onResponseSave();
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }
  if (error) {
    return <div>{error}</div>;
  }

  const renderQuestion = () => {
    if (answerType === QUESTION_TYPES.SINGLE_ANSWER_MULTI_CHOICE) {
      return (
        <JeddleSingleAnswerMultiChoiceQuestion
          question={question}
          quizStats={quizStats}
          response={response}
          showResult={showResult}
          isSaving={isSaving}
          isAnswered={mode === MODE.answered}
          isCorrect={correctness}
          onResponseChange={handleResponseChange}
          onSaveClick={handleSaveClick}
          onContinue={handleContinue}
        />
      );
    }

    if (answerType === QUESTION_TYPES.FREE_TEXT) {
      return (
        <JeddleFreeTextQuestion
          question={question}
          response={response}
          isSaving={isSaving}
          isAnswered={mode === MODE.answered}
          onResponseChange={handleResponseChange}
          onSaveClick={handleSaveClick}
          onContinue={handleContinue}
        />
      );
    }

    return <div>Unsupported Jeddle question type</div>;
  };

  return (
    <div className="chunk-question" style={style}>
      <div className="container">{renderQuestion()}</div>
    </div>
  );
};

JeddleQuestion.propTypes = {
  question: quizQuestionPropType.isRequired,
  lesson: lessonPropType.isRequired,
  lessonQuizAttempts: IPT.listOf(quizAttemptPropType),
  onResponseSave: PT.func.isRequired,
  saveQuizAttempt: PT.func.isRequired,
  nextChunkUrl: PT.string,
  isLastChunk: PT.bool,
  style: PT.shape({
    minHeight: PT.number,
  }),
};

export default JeddleQuestion;
